import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AddMenuAPI } from '../redux/actions/Menu/AddMenuAction';
import {GetFoodCategoriesDropdownAPI} from '../redux/actions/Content/GetFoodCategoryDropdown'
import { showErrorToast, showSuccessToast } from '../helpers/SweetAlert';
import { useNavigate } from 'react-router-dom';

const AddMenu = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedFoodCategory, setSelectedFoodCategory] = useState('');
  const [foodName, setFoodName] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [images, setImages] = useState([]);
  const [selectedDay, setSelectedDay] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  
  const { response: addMenuResponse, error: addMenuError } = useSelector((state) => state.addMenu);
  const { response: foodCategoriesResponse, loading: foodCategoriesLoading } = useSelector((state) => state.getFoodCategoryDropdown);

  // Fetch food categories on component mount
  useEffect(() => {
    dispatch(GetFoodCategoriesDropdownAPI());
  }, [dispatch]);

  // Allowed image types
  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

  // Handle file selection
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const validImages = [];

    // Check if total files exceed the limit (e.g., 5 images)
    if (images.length + files.length > 5) {
      showErrorToast("Maximum upload limit is 5 images.");
      return;
    }

    for (let file of files) {
      if (!allowedTypes.includes(file.type)) {
        showErrorToast(`Invalid file type: ${file.name}. Only images are allowed.`);
        continue;
      }

      if (file.size > 2 * 1024 * 1024) {
        showErrorToast(`File ${file.name} exceeds 2MB size limit.`);
        continue;
      }

      validImages.push({
        file,
        url: URL.createObjectURL(file)
      });
    }

    setImages(prev => [...prev, ...validImages].slice(0, 5)); // Max 5 images
  };

  // Handle drag and drop
  const handleDrop = (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    handleFileChange({ target: { files } });
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  // Remove image from preview
  const handleRemoveImage = (indexToRemove) => {
    setImages(prev => prev.filter((_, index) => index !== indexToRemove));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!selectedCategory) newErrors.category = true;
    if (!selectedFoodCategory) newErrors.foodCategory = true;
    if (!foodName.trim()) newErrors.name = true;
    if (!description.trim()) newErrors.description = true;
    if (!price) newErrors.price = true;
    if (images.length === 0) newErrors.images = true;
    if (selectedDay === '') newErrors.day = true;

    setValidationErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) return;

    const formData = new FormData();
    
    // Append images
    images.forEach((imgObj) => {
      formData.append('images', imgObj.file);
    });

    // Append other form data
    formData.append('category', selectedCategory === 'veg' ? '0' : '1'); 
    formData.append('foodCategory', selectedFoodCategory);
    formData.append('name', foodName);
    formData.append('description', description);
    formData.append('price', price);
    formData.append('day', selectedDay);

    dispatch(AddMenuAPI(formData));
  };

  useEffect(() => {
    if (addMenuResponse?.status) {
      showSuccessToast(addMenuResponse.message);
      resetFormFields();
    }
    if (addMenuError) {
      showErrorToast(addMenuError.data);
    }
  }, [addMenuResponse, addMenuError]);

  const resetFormFields = () => {
    setSelectedCategory('');
    setSelectedFoodCategory('');
    setFoodName('');
    setDescription('');
    setPrice('');
    setSelectedDay('');
    setImages([]);
    setValidationErrors({});
  };

  return (
    <div className='content_section'>
      <div className='container'>
        <div className='row'>
          <div className='col-lg-10'>
            <div className='row gy-4'>
              <div className='col-md-4'>
                <div className='input_wrapper'>
                  <label>Food Type</label>
                  <div className='form_field'>
                    <select 
                      className={`form-select ${validationErrors.category ? 'is-invalid' : ''}`} 
                      value={selectedCategory}
                      onChange={(e) => {
                        setSelectedCategory(e.target.value);
                        if (validationErrors.category) {
                          setValidationErrors(prev => ({ ...prev, category: false }));
                        }
                      }}
                    >
                      <option value="">Select Food Type</option>
                      <option value="veg">Veg</option>
                      <option value="non-veg">Non-Veg</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className='col-md-4'>
                <div className='input_wrapper'>
                  <label>Day</label>
                  <div className='form_field'>
                    <select 
                      className={`form-select ${validationErrors.day ? 'is-invalid' : ''}`} 
                      value={selectedDay}
                      onChange={(e) => {
                        setSelectedDay(e.target.value);
                        if (validationErrors.day) {
                          setValidationErrors(prev => ({ ...prev, day: false }));
                        }
                      }}
                    >
                      <option value="">Select Day</option>
                      <option value="0">Monday</option>
                      <option value="1">Tuesday</option>
                      <option value="2">Wednesday</option>
                      <option value="3">Thursday</option>
                      <option value="4">Friday</option>
                      <option value="5">Saturday</option>
                      <option value="6">Sunday</option>
                    </select>
                  </div>
                </div>
              </div>
              
              <div className='col-md-4'>
                <div className='input_wrapper'>
                  <label>Food Category</label>
                  <div className='form_field'>
                    <select 
                      className={`form-select ${validationErrors.foodCategory ? 'is-invalid' : ''}`} 
                      value={selectedFoodCategory}
                      onChange={(e) => {
                        setSelectedFoodCategory(e.target.value);
                        if (validationErrors.foodCategory) {
                          setValidationErrors(prev => ({ ...prev, foodCategory: false }));
                        }
                      }}
                      disabled={foodCategoriesLoading}
                    >
                      <option value="">Select Food Category</option>
                      {foodCategoriesResponse?.data?.map((category) => (
                        <option key={category._id} value={category._id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                    {foodCategoriesLoading && (
                      <div className="mt-2 text-muted">Loading categories...</div>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Image Upload Section */}
              <div className='col-md-12'>
                <div className='input_wrapper'>
                  <label>Upload Images</label>
                  <div 
                    className='form_field form_field_upload' 
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                  >
                    <input 
                      type="file" 
                      className='d-none' 
                      id='uploadMenuImg' 
                      multiple 
                      onChange={handleFileChange} 
                      accept="image/jpeg, image/png, image/jpg, image/webp" 
                    />
                    <label htmlFor='uploadMenuImg'>
                      <span><img src='/assets/icons/upload-icon.svg' alt="upload" /></span>
                      <span>Drag or drop here</span>
                      <span>or</span>
                      <span className='primary_text'>Browse</span>
                    </label>
                  </div>
                  <span className="required_info">
                    Maximum 2MB image size and only accepted jpeg, png, jpg, webp. Maximum upload limit is 5.
                  </span>
                  <div className='image-preview-list mt-3 d-flex flex-wrap gap-2'>
                    {images.map((img, index) => (
                      <div key={index} className='preview-img-box position-relative'>
                        <img
                          src={img.url}
                          alt={`preview-${index}`}
                          style={{
                            width: '80px',
                            height: '80px',
                            objectFit: 'cover',
                            borderRadius: '8px',
                            border: '1px solid #ddd',
                          }}
                        />
                        <button
                          type='button'
                          onClick={() => handleRemoveImage(index)}
                          style={{
                            position: 'absolute',
                            top: '-6px',
                            right: '-6px',
                            backgroundColor: '#fff',
                            border: '1px solid #ccc',
                            borderRadius: '50%',
                            width: '20px',
                            height: '20px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '12px',
                            cursor: 'pointer',
                          }}
                        >
                          &times;
                        </button>
                      </div>
                    ))}
                  </div>
                  {validationErrors.images && (
                    <div className="invalid-feedback d-block">
                      Please upload at least one image
                    </div>
                  )}
                </div>
              </div>
              
              <div className='col-md-6'>
                  <div className='input_wrapper'>
                    <label>Price</label>
                    <div className='form_field'>
                      <input 
                        type='text' 
                        className={`form-control ${validationErrors.price ? 'is-invalid' : ''}`} 
                        placeholder='Enter price' 
                        value={price}
                        onChange={(e) => {
                          // Only allow numbers and a single decimal point
                          const value = e.target.value;
                          if (value === '' || /^\d*\.?\d*$/.test(value)) {
                            setPrice(value);
                          }
                          if (validationErrors.price) {
                            setValidationErrors(prev => ({ ...prev, price: false }));
                          }
                        }}
                        onKeyDown={(e) => {
                          // Prevent typing alphabets and special characters
                          if (!/[0-9.]/.test(e.key) && 
                              e.key !== 'Backspace' && 
                              e.key !== 'Delete' && 
                              e.key !== 'ArrowLeft' && 
                              e.key !== 'ArrowRight') {
                            e.preventDefault();
                          }
                        }}
                        inputMode="numeric"
                      />
                    </div>
                  </div>
                </div>

              <div className='col-md-6'>
                <div className='input_wrapper'>
                  <label>Food Name</label>
                  <div className='form_field'>
                    <input 
                      type='text' 
                      className={`form-control ${validationErrors.name ? 'is-invalid' : ''}`} 
                      placeholder='Enter food name' 
                      value={foodName} 
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value.length === 1 && value === ' ') return;
                        setFoodName(value);
                        if (validationErrors.name) {
                          setValidationErrors(prev => ({ ...prev, name: false }));
                        }
                      }} 
                    />
                  </div>
                </div>
              </div>
              
              
              <div className='col-md-12'>
                <div className='input_wrapper'>
                  <label>Description</label>
                  <div className='form_field'>
                    <textarea 
                      className={`form-control ${validationErrors.description ? 'is-invalid' : ''}`} 
                      placeholder='Enter description' 
                      rows={6} 
                      value={description} 
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value.length === 1 && value === ' ') return;
                        setDescription(value);
                        if (validationErrors.description) {
                          setValidationErrors(prev => ({ ...prev, description: false }));
                        }
                      }}
                    />
                  </div>
                </div>
              </div>
              
              <div className='col-12'>
                <div className='hstack gap-3 mt-4'>
                  <button className='primary-btn' onClick={handleSubmit}>Save</button>
                  <button className='transparent-btn' onClick={resetFormFields}>Clear</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddMenu;